'use client';

import { MascotWithName } from '@spikemark/ui/components/mascot-with-name';
import { PlayerLink, TeamLink } from '@spikemark/ui/components/navigation/entity-links';
import { TableColumn } from '@spikemark/ui/components/table/types';
import { RadioGroup, RadioGroupOption } from '@spikemark/ui/components/ui/radio-group';
import { MatchplayLeaderboardItem, TieWinner } from '@spikemark/ui/types/leaderboard';
import { getLeaderboardRankingDisplay } from '@spikemark/ui/utils/scoring';
import { OverallRankDisplay } from './shared';
const MATCHPLAY_LEADERBOARD_DETAIL_WIDTH = '100px';
type ColumnOptions = {
  isAdmin?: boolean;
  winnerCount?: number;
  onWinnerSelect?: (value: TieWinner) => void;
  winner?: TieWinner;
  teamRankingsDisplayCutoff?: number;
  playerRankingsDisplayCutoff?: number;
};
export const createMatchplayColumns = (mobile: boolean, {
  isAdmin,
  winner,
  onWinnerSelect,
  teamRankingsDisplayCutoff,
  playerRankingsDisplayCutoff
}: ColumnOptions | undefined = {}, allRowData?: MatchplayLeaderboardItem[], positions = allRowData?.map(({
  position
}) => position) ?? []) => {
  const columns: TableColumn<MatchplayLeaderboardItem>[] = [{
    sortIteratee: row => row.position || '-',
    sortable: true,
    width: `75px`,
    key: 'position',
    header: 'POS',
    cellRenderer: ({
      rowData
    }) => getLeaderboardRankingDisplay(positions, rowData.position)
  }, {
    key: 'record',
    header: 'WINNER',
    cellClassName: 'numeric',
    width: mobile ? 31 : 45,
    hidden: !isAdmin,
    cellRenderer: ({
      rowData
    }) => {
      const isSelected = winner === rowData.id;
      return rowData.currentRank === 1 ? <div className="flex justify-center">
            <RadioGroup className="text-clippd-pink mx-auto" onClick={() => onWinnerSelect?.({
          id: rowData.id
        })}>
              <RadioGroupOption label="" id={'winner'} value={rowData.id} checked={isSelected} />
            </RadioGroup>
          </div> : '';
    }
  }, {
    header: 'Team',
    sortable: true,
    sortIteratee: row => row.displayName,
    cellRenderer: ({
      rowData: row
    }) => row.playerId ? <PlayerLink playerId={row.playerId ?? ''} disabled={isAdmin}>
            <MascotWithName alt={row.displayName} path={row.schoolLogo || ''} variant="small" displayMode="team">
              {row.displayName}
            </MascotWithName>
          </PlayerLink> : <TeamLink schoolId={row.id}>
            <MascotWithName alt={row.displayName} path={row.schoolLogo || ''} variant="small" displayMode="team">
              {row.displayName}
            </MascotWithName>
          </TeamLink>,
    width: mobile ? '200px' : undefined
  }, {
    header: 'Team Rank',
    key: 'overallRank',
    width: 110,
    hidden: !teamRankingsDisplayCutoff,
    cellRenderer: ({
      rowData
    }) => <OverallRankDisplay overallRank={rowData.overallRank} displayCutoff={rowData.playerId ? playerRankingsDisplayCutoff : teamRankingsDisplayCutoff} />
  }, {
    header: mobile ? 'W' : 'Win',
    sortIteratee: row => row.record.wins,
    cellRenderer: ({
      rowData: row
    }) => <span className="px-1">{`${row.record.wins}`}</span>,
    sortable: true,
    width: mobile ? '60px' : MATCHPLAY_LEADERBOARD_DETAIL_WIDTH
  }, {
    header: mobile ? 'L' : 'Loss',
    sortIteratee: row => row.record.losses,
    cellRenderer: ({
      rowData: row
    }) => <span className="px-1">{`${row.record.losses}`}</span>,
    sortable: true,
    width: mobile ? '60px' : MATCHPLAY_LEADERBOARD_DETAIL_WIDTH
  }, {
    header: mobile ? 'T' : 'Tie',
    sortIteratee: row => row.record.draws,
    cellRenderer: ({
      rowData: row
    }) => <span className="px-1">{`${row.record.draws}`}</span>,
    sortable: true,
    width: mobile ? '60px' : MATCHPLAY_LEADERBOARD_DETAIL_WIDTH
  }];
  return columns;
};